import { useEffect, useRef, useState, useCallback } from 'react'

export interface WSOptions {
  onMessage?: (data: unknown) => void
  onOpen?: () => void
  onClose?: () => void
  reconnectDelayMs?: number
  maxReconnectAttempts?: number
}

export interface WSState {
  connected: boolean
  lastEvent: unknown
  error: string | null
  reconnectCount: number
}

export function useWebSocket(url: string | null, options: WSOptions = {}): WSState {
  const {
    reconnectDelayMs = 3000,
    maxReconnectAttempts = 10,
  } = options

  // Keep callbacks in refs so they never need to be in useCallback deps.
  // This prevents the connect function from changing on every render,
  // which would close a still-connecting socket ("closed before established").
  const onMessageRef = useRef(options.onMessage)
  const onOpenRef    = useRef(options.onOpen)
  const onCloseRef   = useRef(options.onClose)
  useEffect(() => {
    onMessageRef.current = options.onMessage
    onOpenRef.current    = options.onOpen
    onCloseRef.current   = options.onClose
  })

  const [state, setState] = useState<WSState>({
    connected: false,
    lastEvent: null,
    error: null,
    reconnectCount: 0,
  })

  const wsRef             = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectCountRef = useRef(0)
  const mountedRef        = useRef(true)

  // connect is stable: only url / reconnect params are deps (no callbacks)
  const connect = useCallback(() => {
    if (!url) return                                          // null → disabled
    if (!mountedRef.current) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return }
        reconnectCountRef.current = 0
        setState(s => ({ ...s, connected: true, error: null, reconnectCount: 0 }))
        onOpenRef.current?.()
      }

      ws.onmessage = (evt) => {
        if (!mountedRef.current) return
        let parsed: unknown = evt.data
        try { parsed = JSON.parse(evt.data as string) } catch { /* keep raw */ }
        setState(s => ({ ...s, lastEvent: parsed }))
        onMessageRef.current?.(parsed)
      }

      ws.onclose = () => {
        if (!mountedRef.current) return
        setState(s => ({ ...s, connected: false }))
        onCloseRef.current?.()

        if (reconnectCountRef.current < maxReconnectAttempts) {
          reconnectCountRef.current += 1
          const delay = Math.min(
            reconnectDelayMs * Math.pow(1.5, reconnectCountRef.current - 1),
            30000,
          )
          reconnectTimerRef.current = setTimeout(connect, delay)
          setState(s => ({ ...s, reconnectCount: reconnectCountRef.current }))
        } else {
          setState(s => ({
            ...s,
            error: `WebSocket disconnected after ${maxReconnectAttempts} reconnect attempts`,
          }))
        }
      }

      ws.onerror = () => {
        if (!mountedRef.current) return
        setState(s => ({ ...s, error: 'WebSocket connection error' }))
      }
    } catch (err) {
      setState(s => ({
        ...s,
        error: err instanceof Error ? err.message : 'Failed to connect',
      }))
    }
  }, [url, reconnectDelayMs, maxReconnectAttempts]) // callbacks intentionally omitted — read via refs

  useEffect(() => {
    mountedRef.current = true
    connect()

    return () => {
      mountedRef.current = false
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (wsRef.current) {
        wsRef.current.onclose = null // prevent reconnect on intentional close
        wsRef.current.close()
      }
    }
  }, [connect])

  return state
}
